// Acciones Masivas de Premio Elite (ver informe "LYNX — Agregar acciones
// masivas de Premio Elite"): NO reemplaza los Ajustes Manuales por chofer
// ni por día (DriverDetailModal.tsx) — es una forma rápida de aplicar la
// MISMA decisión a todos los choferes de la liquidación de una sola vez.
// Reutiliza exactamente PUT /v1/liquidaciones/choferes/bulk-adjustments,
// que por dentro llama el mismo mecanismo de Settlement Adjustments de
// siempre (insertDriverAdjustment), solo que recorriendo todos los
// Drivers del período — nunca un cálculo nuevo.
import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";

type Direction = "GANADO" | "PERDIDO";
type Scope = "WEEK" | "DAY";

const DAY_NAMES = ["Viernes", "Sábado", "Domingo", "Lunes", "Martes", "Miércoles", "Jueves"];

// period.from es "YYYY-MM-DD" (viernes) — arma las 7 fechas Viernes..Jueves
// en UTC puro (mismo criterio que el resto de la app para no arrastrar
// desfases de huso al sumar días sobre un date-key).
function periodDayOptions(fromKey: string): { dateKey: string; label: string }[] {
  const [y, m, d] = fromKey.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  return DAY_NAMES.map((name, i) => {
    const dt = new Date(base.getTime());
    dt.setUTCDate(dt.getUTCDate() + i);
    const dateKey = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
    return { dateKey, label: `${name} ${String(dt.getUTCDate()).padStart(2, "0")}/${String(dt.getUTCMonth() + 1).padStart(2, "0")}` };
  });
}

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 170, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const optionRowStyle: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const radioCardStyle = (active: boolean): React.CSSProperties => ({
  flex: "1 1 160px",
  padding: "12px 14px",
  borderRadius: "12px",
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-soft)" : "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "13px",
  fontWeight: 600,
  color: "var(--text-primary)",
});
const summaryBoxStyle: React.CSSProperties = {
  padding: "14px 16px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt, transparent)",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  fontSize: "13px",
};

export default function BulkEliteActionModal({
  open,
  direction,
  period,
  token,
  onClose,
  onApplied,
}: {
  open: boolean;
  direction: Direction | null;
  period: { key: string; from: string; fromDisplay: string; toDisplay: string } | null;
  token: string | null;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [scope, setScope] = useState<Scope>("WEEK");
  const [scopeDate, setScopeDate] = useState<string>("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ appliedCount: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep("form");
    setScope("WEEK");
    setScopeDate("");
    setReason("");
    setError(null);
    setResult(null);
  }, [open, direction]);

  if (!open || !direction || !period) return null;

  const dayOptions = periodDayOptions(period.from);
  const actionLabel = direction === "GANADO" ? "Aplicar Premio Elite" : "Anular Premio Elite";
  const scopeLabel = scope === "WEEK" ? "Semana completa" : dayOptions.find((d) => d.dateKey === scopeDate)?.label || "—";

  const handleContinue = () => {
    if (scope === "DAY" && !scopeDate) {
      setError("Elegí un día.");
      return;
    }
    if (!reason.trim()) {
      setError("El motivo es obligatorio.");
      return;
    }
    setError(null);
    setStep("confirm");
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(`/v1/liquidaciones/choferes/bulk-adjustments`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({
          period: period.key,
          scope,
          scope_date: scope === "DAY" ? scopeDate : undefined,
          direction,
          reason: reason.trim(),
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo aplicar la acción masiva");
      setResult({ appliedCount: payload.appliedCount });
      onApplied();
    } catch (e: any) {
      setError(e.message || "No se pudo aplicar la acción masiva");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={() => !submitting && onClose()} />
      <div style={{ position: "relative", width: "100%", maxWidth: "520px" }}>
        <Card padding="20px" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "var(--text-primary)" }}>
            ⚡ {actionLabel} a Todos
          </h3>

          {result ? (
            <>
              <div style={summaryBoxStyle}>
                <span>✅ Acción aplicada correctamente.</span>
                <span>{result.appliedCount} ajuste(s) creado(s) — un ajuste por cada chofer con actividad en el alcance elegido, con trazabilidad completa (usuario, fecha, hora, alcance, motivo).</span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={onClose}>Cerrar</Button>
              </div>
            </>
          ) : step === "form" ? (
            <>
              <div>
                <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Alcance</p>
                <div style={optionRowStyle}>
                  <button type="button" style={radioCardStyle(scope === "WEEK")} onClick={() => setScope("WEEK")}>
                    ○ Toda la semana
                  </button>
                  <button type="button" style={radioCardStyle(scope === "DAY")} onClick={() => setScope("DAY")}>
                    ○ Un día específico
                  </button>
                </div>
              </div>

              {scope === "DAY" && (
                <div>
                  <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 700, color: "var(--text-primary)" }}>Día</p>
                  <div style={optionRowStyle}>
                    {dayOptions.map((d) => (
                      <button key={d.dateKey} type="button" style={radioCardStyle(scopeDate === d.dateKey)} onClick={() => setScopeDate(d.dateKey)}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Input
                label="Motivo (obligatorio)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: Decisión operativa general de la semana"
              />

              {error && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{error}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                <Button onClick={handleContinue}>Continuar</Button>
              </div>
            </>
          ) : (
            <>
              <div style={summaryBoxStyle}>
                <div>Se aplicará:</div>
                <div style={{ fontWeight: 700, color: direction === "GANADO" ? "var(--success, #2ea043)" : "var(--danger, #d1242f)" }}>
                  {direction === "GANADO" ? "✔" : "✘"} {actionLabel}
                </div>
                <div><span style={{ color: "var(--text-secondary)" }}>Alcance: </span>{scopeLabel}</div>
                <div style={{ fontWeight: 700 }}>Esto afectará a TODOS los choferes de la liquidación.</div>
                <div><span style={{ color: "var(--text-secondary)" }}>Motivo: </span>{reason.trim()}</div>
              </div>

              {error && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{error}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <Button variant="secondary" onClick={() => setStep("form")} disabled={submitting}>Volver</Button>
                <Button onClick={handleConfirm} disabled={submitting}>{submitting ? "Aplicando…" : "Confirmar"}</Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
